import React, { useEffect, useState, createContext } from 'react';
import mapboxgl from 'mapbox-gl';

import { useSettingsContext } from '@mono/platmap-settings-context';
import { diff } from '@mono/diff';
import { Layer, GeojsonLayer, PlatmapLayerContext } from '@mono/types/src/platmap';
import { toHash } from '@mono/to-hash';
import { isGeojsonLayer } from 'layerTypeUtils';

export const GeojsonContext = createContext<PlatmapLayerContext>({
    setMap: () => {},
    shuffle: () => {},
});

interface GeojsonContextProviderProps {
    children: JSX.Element;
    delimiter: string;
    anchorId: string;
}
export const GeojsonContextProvider = ({ children, delimiter, anchorId }: GeojsonContextProviderProps) => {
    const [state, setState] = useState<{
        map: mapboxgl.Map | null;
        shuffleIndex: string;
    }>({
        map: null,
        shuffleIndex: 'The sqrt(-1), characterised as i, was imagined first - discovered in nature second',
    });
    const { settings } = useSettingsContext();
    const geojsonLayers = settings.layers.filter(isGeojsonLayer);

    useEffect(() => {
        if (!state.map) return;
        const mapboxStyle = state.map.getStyle();
        const mapboxLayers = mapboxStyle.layers;
        if (!mapboxLayers) return;

        const existingSources = Object.keys(mapboxStyle.sources).filter((sourceId) => {
            if (sourceId.includes(anchorId)) return false;
            return sourceId.startsWith(['plat', 'geojson', ''].join(delimiter));
        });
        const targetSources = settings.layers.filter(isGeojsonLayer).map((layer) => {
            return ['plat', 'geojson', layer.id].join(delimiter);
        });

        const { add: sourcesToAdd, remove: sourcesToRemove } = diff(existingSources, targetSources);

        for (const sourceIndex of sourcesToAdd) {
            const [, , layerId] = sourceIndex.split(delimiter);
            const layer = geojsonLayers.find((layer) => layer.id === layerId);
            if (!layer) continue;
            state.map.addSource(sourceIndex, {
                type: 'geojson',
                data: layer.state.geojson,
                ...layer.state.sourceProperties,
            });
        }

        const existingLayers = mapboxLayers
            .filter((l) => {
                if (!l.id.startsWith(['plat', 'geojson', ''].join(delimiter))) return false;
                if (l.id.includes(anchorId)) return false;
                return true;
            })
            .map((a) => a.id);

        const targetLayers = settings.layers
            .filter((l): l is Layer & { state: GeojsonLayer } => {
                if (l.state.type !== 'geojson') return false;
                return true;
            })
            .map((l) => l.state.layers.map((gl) => ['plat', 'geojson', l.id, gl.id].join(delimiter)))
            .flat();

        const { add, remove } = diff(existingLayers, targetLayers);

        for (const layerIndex of remove) {
            if (!state.map) return;
            state.map.removeLayer(layerIndex);
        }

        for (const sourceId of sourcesToRemove) {
            state.map.removeSource(sourceId);
        }

        // This if is a poll to check if the anchor layers have finished being added to mapbox.
        // Better to subscribe and be notified, but mapboxgl doesn't support that. They'd be a lot cooler if they did.
        if (
            existingLayers.some((layerIndex) => {
                const [, , layerId] = layerIndex.split(delimiter);
                const anchorIndex = ['plat', 'geojson', layerId, anchorId].join(delimiter);

                return !mapboxLayers.find((l) => l.id === anchorIndex);
            })
        ) {
            console.log('waiting for anchor layers to be added');
            setTimeout(() => {
                setState((prev) => ({ ...prev, shuffleIndex: prev.shuffleIndex + 'x' }));
            }, 16);
            return;
        }

        for (const layerIndex of add) {
            const [, , layerId, geojsonLayerId] = layerIndex.split(delimiter);
            const anchorIndex = ['plat', 'geojson', layerId, anchorId].join(delimiter);

            const layer = geojsonLayers.find((l) => l.id === layerId);
            if (!layer) continue;
            const geojsonLayer = layer.state.layers.find((l) => l.id === geojsonLayerId);
            if (!geojsonLayer) continue;

            state.map.addLayer(
                {
                    ...geojsonLayer,
                    source: ['plat', 'geojson', layerId].join(delimiter),
                    id: layerIndex,
                },
                anchorIndex
            );
        }

        for (const layer of settings.layers) {
            if (layer.state.type !== 'geojson') continue;
            const layers = mapboxStyle.layers
                .map((l) => l.id)
                .filter((l) => l.startsWith(['plat', 'geojson', layer.id].join(delimiter)))
                .filter((l) => !l.includes('anchor'));
            if (!layers) continue;

            const layerAnchorId = ['plat', 'geojson', layer.id, anchorId].join(delimiter);
            for (const layerIndex of layers) {
                state.map.moveLayer(layerIndex, layerAnchorId);
            }
        }
    }, [state.shuffleIndex, geojsonLayers.map((l) => l.id).join()]);

    useEffect(() => {
        for (const layer of geojsonLayers) {
            const layerIndex = ['plat', 'geojson', layer.id].join(delimiter);
            if (!state.map) continue;
            const source = state.map.getSource(layerIndex);
            if (!source) continue;

            //@ts-ignore todo
            source.setData(layer.state.geojson);
        }
    }, [toHash(geojsonLayers.map((l) => l.state.geojson))]);

    useEffect(() => {
        if (!state.map) return;
        const mapboxStyle = state.map.getStyle();
        const mapboxLayers = mapboxStyle.layers;
        if (!mapboxLayers) return;

        const images = geojsonLayers.reduce<Record<string, string>>((acc, layer) => {
            if (!layer.state.icons) return acc;
            const icons = layer.state.icons;
            return { ...acc, ...icons };
        }, {});

        Object.keys(images).forEach((name) => {
            if (!state.map) return;
            if (state.map.hasImage(name)) return;

            state.map.loadImage(images[name], (error, image) => {
                if (error) throw error;
                if (!image || !state.map) return;
                state.map.addImage(name, image);
            });
        });
    }, [state.map, toHash(geojsonLayers.map((l) => l.state.icons))]);

    return (
        <GeojsonContext.Provider
            value={{
                setMap: (map: mapboxgl.Map) => setState((state) => ({ ...state, map })),
                shuffle: (shuffleIndex: string) => {
                    setState((state) => ({ ...state, shuffleIndex }));
                },
            }}
        >
            {children}
        </GeojsonContext.Provider>
    );
};
